import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useUpdateLesson } from '../../hooks/api/lesson';
import type { LessonAccessLevel } from '../../hooks/api/lesson';
import { useGetUsers } from '../../hooks/api/user';
import { convertDateToDateString, convertDateToTimeInputString } from '../../util/dates';

interface UpdateLessonModalProps {
  lessonId: number;
  isOpen: boolean;
  close: () => void;
  initialValues: {
    startDate: string;
    endDate: string;
    type: string;
    accessLevel: LessonAccessLevel;
    professorId: number;
  };
}

export default function UpdateLessonModal({
  close,
  isOpen,
  initialValues,
  lessonId,
}: UpdateLessonModalProps) {
  const defaultValues = useMemo(() => {
    const startDate = new Date(initialValues.startDate);
    const endDate = new Date(initialValues.endDate);

    return {
      date: convertDateToDateString(startDate),
      startTime: convertDateToTimeInputString(startDate),
      endTime: convertDateToTimeInputString(endDate),
      type: initialValues.type,
      accessLevel: initialValues.accessLevel,
      professorId: initialValues.professorId,
    };
  }, [
    initialValues.accessLevel,
    initialValues.endDate,
    initialValues.professorId,
    initialValues.startDate,
    initialValues.type,
  ]);

  const [date, setDate] = useState(defaultValues.date);
  const [startTime, setStartTime] = useState(defaultValues.startTime);
  const [endTime, setEndTime] = useState(defaultValues.endTime);
  const [type, setType] = useState(defaultValues.type);
  const [accessLevel, setAccessLevel] = useState(defaultValues.accessLevel);
  const [professorId, setProfessorId] = useState(defaultValues.professorId);
  const { data: users } = useGetUsers({ instructor: true });
  const updateLessonMutation = useUpdateLesson();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDate(defaultValues.date);
    setStartTime(defaultValues.startTime);
    setEndTime(defaultValues.endTime);
    setType(defaultValues.type);
    setAccessLevel(defaultValues.accessLevel);
    setProfessorId(defaultValues.professorId);
  }, [defaultValues, isOpen]);

  const handleSubmit = () => {
    const startDateTime = new Date(`${date}T${startTime}`);
    const endDateTime = new Date(`${date}T${endTime}`);
    const hasStartDateChanged = date !== defaultValues.date || startTime !== defaultValues.startTime;
    const hasEndDateChanged = date !== defaultValues.date || endTime !== defaultValues.endTime;

    updateLessonMutation.mutate({
      id: lessonId,
      changeset: {
        type: type !== defaultValues.type ? type : undefined,
        accessLevel: accessLevel !== defaultValues.accessLevel ? accessLevel : undefined,
        startDate: hasStartDateChanged ? startDateTime.toISOString() : undefined,
        endDate: hasEndDateChanged ? endDateTime.toISOString() : undefined,
        professorId: professorId !== defaultValues.professorId ? professorId : undefined,
      },
    });
    close();
  };
  return (
    <Dialog open={isOpen} onClose={close}>
      <DialogTitle>Editar clase</DialogTitle>
      <DialogContent>
        <TextField
          label="Fecha"
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <TextField
          label="Hora comienzo"
          type="time"
          fullWidth
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
        />
        <TextField
          label="Hora fin"
          type="time"
          fullWidth
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
        />
        <TextField
          label="Tipo"
          type="text"
          fullWidth
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel id="access-level-label">Acceso</InputLabel>
          <Select
            label="Acceso"
            labelId="access-level-label"
            value={accessLevel}
            onChange={(event) => setAccessLevel(event.target.value as LessonAccessLevel)}
          >
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="pro">Pro</MenuItem>
            <MenuItem value="morning">Mañana</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="professor-label">Profesor</InputLabel>
          <Select
            label={'Profesor'}
            labelId={'professor-label'}
            value={professorId}
            onChange={(event) => {
              setProfessorId(Number(event.target.value));
            }}
          >
            {users?.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {`${user.firstName} ${user.lastName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancelar</Button>
        <Button onClick={handleSubmit}>Guardar cambios</Button>
      </DialogActions>
    </Dialog>
  );
}
